// frontend/src/pages/employee/FieldVisits.js
import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import axios from 'axios';
import api from '../../api';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { FaWalking, FaPlus, FaSearch, FaFilter, FaEye, FaEdit, FaTimes, FaMapMarkerAlt, FaUser, FaBuilding, FaList, FaAddressBook, FaChevronDown, FaChevronUp, FaPhone } from 'react-icons/fa';
import { AuthContext } from '../../components/AuthContext';
import MasterSelection from '../../components/MasterSelection';

// ── Constants ─────────────────────────────────────────────────────────────────

const VISIT_TYPES    = ['New', 'Revisit'];
const PROJECT_TYPES  = ['Builder', 'Commercial', 'Residence'];
const CONTACT_TYPES  = ['Customer', 'Reference', 'Dealer', 'Guest'];
const ITEMS_OPTIONS  = ['Tiles', 'Sanitary Ware', 'Kitchen', 'Doors', 'Windows', 'Others'].map(v => ({ value: v, label: v }));
const STATUS_OPTIONS = ['Pending', 'Positive', 'Negative', 'No Response', 'Reschedule'];

const STATUS_COLORS = {
  Pending:      'bg-yellow-100 text-yellow-800',
  Positive:     'bg-green-100  text-green-800',
  Negative:     'bg-red-100    text-red-800',
  'No Response':'bg-gray-100   text-gray-700',
  Reschedule:   'bg-blue-100   text-blue-800',
};

const RESPONSE_BUTTONS = [
  { label: 'Positive',    status: 'Positive',    cls: 'bg-green-600 hover:bg-green-700 text-white' },
  { label: 'Negative',   status: 'Negative',    cls: 'bg-red-600   hover:bg-red-700   text-white' },
  { label: 'No Response',status: 'No Response', cls: 'bg-gray-500  hover:bg-gray-600  text-white' },
  { label: '🔁 Reschedule', status: 'Reschedule', cls: 'bg-blue-600 hover:bg-blue-700 text-white' },
];

const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const contactLabel = (visit) => {
  switch (visit.contactType) {
    case 'Customer':  return visit.customer?.name  || '—';
    case 'Reference': return visit.reference?.name || '—';
    case 'Dealer':    return visit.dealer?.businessName || '—';
    case 'Guest':     return visit.guest || '—';
    default:          return '—';
  }
};

// ── Component ─────────────────────────────────────────────────────────────────

const FieldVisitManagement = () => {
  const { user } = useContext(AuthContext);
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // list state
  const [visits, setVisits]         = useState([]);
  const [loading, setLoading]       = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  // filters
  const [search, setSearch]               = useState('');
  const [filterStatus, setFilterStatus]   = useState('');
  const [filterVisitType, setFilterVisitType]   = useState('');
  const [filterContactType, setFilterContactType] = useState('');
  const [filterProjectType, setFilterProjectType] = useState('');
  const [filterFrom, setFilterFrom]       = useState('');
  const [filterTo, setFilterTo]           = useState('');
  const [showFilters, setShowFilters]     = useState(false);

  // modals
  const [showForm, setShowForm]             = useState(false);
  const [showViewModal, setShowViewModal]   = useState(false);
  const [selectedVisit, setSelectedVisit]   = useState(null);
  const [showMasterSelection, setShowMasterSelection] = useState(false);
  const [selectedContact, setSelectedContact]         = useState(null);
  const [selectedContactType, setSelectedContactType] = useState(null);

  // view mode
  const [viewMode, setViewMode] = useState('visits'); // 'visits' | 'contacts'
  const [expandedContact, setExpandedContact] = useState(null);

  // previous visit search (for Revisit type)
  const [prevVisitSearch, setPrevVisitSearch]   = useState('');
  const [prevVisitResults, setPrevVisitResults] = useState([]);
  const [prevVisitLoading, setPrevVisitLoading] = useState(false);

  // form
  const emptyForm = {
    visitType: 'New',
    previousVisit: '',
    projectType: '',
    contactType: '',
    customer: '', reference: '', dealer: '', guest: '',
    contactPersonName: '',
    contactNumber: '',
    location: '',
    items: [],
    remarks: '',
  };
  const [form, setForm] = useState(emptyForm);

  // dark mode
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  useEffect(() => {
    const interval = setInterval(() => setIsDarkMode(localStorage.getItem('darkMode') === 'true'), 500);
    return () => clearInterval(interval);
  }, []);

  const itemsPerPage = 15;

  // ── react-select styles ───────────────────────────────────────────────────
  const selectStyles = useMemo(() => ({
    control: (p) => ({
      ...p,
      backgroundColor: isDarkMode ? '#374151' : 'white',
      borderColor: isDarkMode ? '#4b5563' : '#d1d5db',
      color: isDarkMode ? '#f3f4f6' : '#111827',
      '&:hover': { borderColor: isDarkMode ? '#6b7280' : '#9ca3af' },
    }),
    singleValue: (p) => ({ ...p, color: isDarkMode ? '#f3f4f6' : '#111827' }),
    input: (p) => ({ ...p, color: isDarkMode ? '#f3f4f6' : '#111827' }),
    menu: (p) => ({ ...p, backgroundColor: isDarkMode ? '#374151' : 'white', border: isDarkMode ? '1px solid #4b5563' : undefined }),
    option: (p, s) => ({
      ...p,
      backgroundColor: s.isFocused ? (isDarkMode ? '#4b5563' : '#eff6ff') : 'transparent',
      color: isDarkMode ? '#f3f4f6' : '#111827',
      '&:active': { backgroundColor: isDarkMode ? '#6b7280' : '#dbeafe' },
    }),
    placeholder: (p) => ({ ...p, color: isDarkMode ? '#9ca3af' : '#9ca3af' }),
    multiValue: (p) => ({ ...p, backgroundColor: isDarkMode ? '#4b5563' : '#eff6ff', border: isDarkMode ? '1px solid #6b7280' : '1px solid #3b82f6' }),
    multiValueLabel: (p) => ({ ...p, color: isDarkMode ? '#e5e7eb' : '#1e40af' }),
    multiValueRemove: (p) => ({ ...p, color: isDarkMode ? '#e5e7eb' : '#1e40af', '&:hover': { backgroundColor: isDarkMode ? '#6b7280' : '#3b82f6', color: 'white' } }),
  }), [isDarkMode]);

  // ── API calls ─────────────────────────────────────────────────────────────
  const fetchVisits = useCallback(async (page = currentPage) => {
    setLoading(true);
    try {
      const params = {
        companyId: user?.user?.company,
        userId: user?.user?._id,
        page,
        limit: itemsPerPage,
        ...(search && { search }),
        ...(filterStatus && { status: filterStatus }),
        ...(filterVisitType && { visitType: filterVisitType }),
        ...(filterContactType && { contactType: filterContactType }),
        ...(filterProjectType && { projectType: filterProjectType }),
        ...(filterFrom && { from: filterFrom }),
        ...(filterTo && { to: filterTo }),
      };
      const res = await axios.get(`${API_BASE}/field-visits`, { params });
      if (res.data.success) {
        setVisits(res.data.data);
        setTotalPages(res.data.pagination.total);
        setTotalCount(res.data.pagination.count);
      }
    } catch (err) {
      console.error('FieldVisit fetch error:', err);
      toast.error('Failed to fetch field visits');
    } finally {
      setLoading(false);
    }
  }, [API_BASE, user, currentPage, search, filterStatus, filterVisitType, filterContactType, filterProjectType, filterFrom, filterTo]);

  useEffect(() => { fetchVisits(currentPage); }, [currentPage]); // eslint-disable-line

  const handleSearch = () => { setCurrentPage(1); fetchVisits(1); };

  const searchPreviousVisits = async (q) => {
    if (!q || q.length < 2) { setPrevVisitResults([]); return; }
    setPrevVisitLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/field-visits`, {
        params: { companyId: user?.user?.company, search: q, limit: 10 },
      });
      setPrevVisitResults(res.data.success ? res.data.data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setPrevVisitLoading(false);
    }
  };

  // ── Form submission ───────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Explicit validation
    const phone = form.contactNumber?.trim();
    if (!phone) {
      toast.error('Contact Person Number is required');
      return;
    }
    if (!/^[0-9+\-\s]{7,15}$/.test(phone)) {
      toast.error('Enter a valid phone number (7–15 digits)');
      return;
    }
    if (!form.contactPersonName?.trim()) {
      toast.error('Contact Person Name is required');
      return;
    }

    try {
      const payload = {
        ...form,
        company:   user?.user?.company,
        createdBy: user?.user?._id,
      };
      // clear irrelevant contact fields
      if (form.contactType !== 'Customer')  delete payload.customer;
      if (form.contactType !== 'Reference') delete payload.reference;
      if (form.contactType !== 'Dealer')    delete payload.dealer;
      if (form.contactType !== 'Guest')     delete payload.guest;
      if (form.visitType !== 'Revisit')     delete payload.previousVisit;

      await axios.post(`${API_BASE}/field-visits`, payload);
      toast.success('Field visit recorded');
      // If this is a Revisit, mark the previous visit's reminder as VISITED
      if (form.visitType === 'Revisit' && form.previousVisit) {
        try {
          const remRes = await api.get(`${API_BASE}/reminders/by-ref`, {
            params: { datatype: 'FieldVisit', dataid: form.previousVisit, status: 'PENDING', limit: 1 },
          });
          const existingReminder = remRes.data?.reminders?.[0] || remRes.data?.[0];
          if (existingReminder?._id) {
            await api.put(`${API_BASE}/reminders/status/${existingReminder._id}`, {
              status: 'VISITED',
              details: { notes: 'Revisit created' },
            });
          }
        } catch (remErr) {
          console.error('Could not mark previous reminder as VISITED:', remErr);
        }
      }
      setShowForm(false);
      setForm(emptyForm);
      setSelectedContact(null);
      setSelectedContactType(null);
      fetchVisits(1);
    } catch (err) {
      console.error('FieldVisit create error:', err);
      toast.error(err.response?.data?.message || 'Failed to create field visit');
    }
  };

  // ── Status update ─────────────────────────────────────────────────────────
  const handleStatusUpdate = async (visitId, newStatus) => {
    // Reschedule: ask for date + message and create a reminder
    if (newStatus === 'Reschedule') {
      const dark = isDarkMode;
      const today = new Date().toISOString().split('T')[0];
      const { value: formVals, isConfirmed } = await Swal.fire({
        title: '🔁 Schedule Next Visit',
        width: 480,
        background: dark ? '#1f2937' : '#ffffff',
        color: dark ? '#f3f4f6' : '#111827',
        html: `
          <div style="text-align:left;display:flex;flex-direction:column;gap:12px">
            <div>
              <label style="font-size:13px;font-weight:500;color:${dark ? '#d1d5db':'#374151'}">Next Visit Date *</label>
              <input id="swal-remind-date" type="date" min="${today}" value="${today}"
                style="width:100%;margin-top:4px;padding:8px 12px;border-radius:8px;border:1px solid ${dark?'#4b5563':'#d1d5db'};background:${dark?'#374151':'#fff'};color:${dark?'#f3f4f6':'#111827'};font-size:14px;box-sizing:border-box" />
            </div>
            <div>
              <label style="font-size:13px;font-weight:500;color:${dark ? '#d1d5db':'#374151'}">Message</label>
              <textarea id="swal-remind-msg" rows="3" placeholder="Reminder message…"
                style="width:100%;margin-top:4px;padding:8px 12px;border-radius:8px;border:1px solid ${dark?'#4b5563':'#d1d5db'};background:${dark?'#374151':'#fff'};color:${dark?'#f3f4f6':'#111827'};font-size:14px;resize:none;box-sizing:border-box"></textarea>
            </div>
          </div>`,
        confirmButtonText: 'Schedule',
        showCancelButton: true,
        preConfirm: () => ({
          date: document.getElementById('swal-remind-date').value,
          message: document.getElementById('swal-remind-msg').value.trim(),
        }),
      });
      if (!isConfirmed || !formVals?.date) return;
      try {
        // Check for existing reminder for this visit
        let existingReminder = null;
        try {
          const remRes = await api.get(`${API_BASE}/reminders/by-ref`, {
            params: { datatype: 'FieldVisit', dataid: visitId, status: 'PENDING', limit: 1 },
          });
          existingReminder = remRes.data?.reminders?.[0] || remRes.data?.[0];
        } catch (_) {}

        if (existingReminder?._id) {
          // Push existing reminder forward
          await api.put(`${API_BASE}/reminders/refollow/${existingReminder._id}`, {
            refollowDate: formVals.date,
            refollowRemarks: formVals.message || 'Visit rescheduled',
            message: formVals.message || 'Follow-up field visit',
            date: new Date(formVals.date),
            refollowedBy: user?.user?._id,
            refollowReason: formVals.message || 'Visit rescheduled',
          });
        } else {
          // Create new reminder
          await api.post(`${API_BASE}/reminders`, {
            datatype: 'FieldVisit',
            dataid: visitId,
            category: 'FIELD_VISIT',
            date: formVals.date,
            message: formVals.message || 'Follow-up field visit',
            createdBy: user?.user?._id,
            createdByModel: 'Employee',
          });
        }
        // Update visit status to Reschedule
        await axios.put(`${API_BASE}/field-visits/${visitId}/status`, { status: 'Reschedule' });
        toast.success('Next visit scheduled');
        fetchVisits(currentPage);
        if (selectedVisit?._id === visitId) setSelectedVisit(prev => ({ ...prev, status: 'Reschedule' }));
      } catch (err) {
        console.error(err);
        toast.error('Failed to schedule visit');
      }
      return;
    }

    // Other statuses: simple confirm
    const result = await Swal.fire({
      title: `Mark as ${newStatus}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      background: isDarkMode ? '#1f2937' : '#fff',
      color: isDarkMode ? '#f3f4f6' : '#111827',
    });
    if (!result.isConfirmed) return;
    try {
      await axios.put(`${API_BASE}/field-visits/${visitId}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchVisits(currentPage);
      if (selectedVisit?._id === visitId) setSelectedVisit(prev => ({ ...prev, status: newStatus }));
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // ── MasterSelection callback ──────────────────────────────────────────────
  const handleMasterSelect = (item, entityType) => {
    setSelectedContact(item);
    setSelectedContactType(entityType);
    setForm(prev => ({
      ...prev,
      customer: '', reference: '', dealer: '', guest: '',
      ...(entityType === 'Guest'
        ? { guest: item.guestName || item.name || '' }
        : { [entityType.toLowerCase()]: item._id }
      ),
    }));
  };

  const getContactDisplayName = (item, type) => {
    if (!item) return '';
    switch (type) {
      case 'Guest':     return `${item.guestName} - ${item.phoneNo || ''}`;
      case 'Reference': return `${item.name} - ${item.phoneNumber || ''} (${item.type || ''})`;
      case 'Customer':  return `${item.name} - ${item.phone || ''}`;
      case 'Dealer':    return `${item.businessName} - ${item.phone || ''}`;
      default:          return '';
    }
  };

  // ── Contact view grouping ─────────────────────────────────────────────────
  const contactGroups = useMemo(() => {
    const map = new Map();
    visits.forEach(v => {
      const key = v.contactType === 'Guest'
        ? `guest::${v.guest}`
        : `${v.contactType}::${v.customer?._id || v.reference?._id || v.dealer?._id}`;
      const name = contactLabel(v);
      const number = v.contactType === 'Customer' ? v.customer?.contactNumber
        : v.contactType === 'Reference' ? v.reference?.contactNumber
        : v.contactType === 'Dealer' ? v.dealer?.contactNumber
        : '';
      if (!map.has(key)) map.set(key, { key, name, contactType: v.contactType, number, visits: [] });
      map.get(key).visits.push(v);
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [visits]);

  // ── Tailwind helpers ──────────────────────────────────────────────────────
  const inputCls = `w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'bg-white border-gray-300 text-gray-800'}`;
  const labelCls = `block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;
  const card     = `rounded-xl shadow-md p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className={`p-4 md:p-6 min-h-screen ${isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className={`text-2xl font-bold flex items-center gap-2 ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
          <FaWalking className="text-blue-500" /> Field Visits
          <span className={`ml-2 text-sm font-normal px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>{totalCount}</span>
        </h2>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className={`flex rounded-lg border overflow-hidden ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
            <button
              onClick={() => setViewMode('visits')}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${
                viewMode === 'visits'
                  ? 'bg-blue-600 text-white'
                  : isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FaList className="text-xs" /> Visits
            </button>
            <button
              onClick={() => setViewMode('contacts')}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-colors ${
                viewMode === 'contacts'
                  ? 'bg-blue-600 text-white'
                  : isDarkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <FaAddressBook className="text-xs" /> Contacts
            </button>
          </div>
          <button onClick={() => { setForm(emptyForm); setSelectedContact(null); setSelectedContactType(null); setShowForm(true); }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
            <FaPlus /> New Visit
          </button>
        </div>
      </div>

      {/* Search & filter bar */}
      <div className={`${card} mb-4`}>
        <div className="flex gap-2 flex-wrap items-center">
          <div className="flex-1 min-w-[200px] relative">
            <FaSearch className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Search by ID, location, guest, remarks…"
              className={inputCls + ' pl-9'}
            />
          </div>
          <button onClick={handleSearch} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm">Search</button>
          <button onClick={() => setShowFilters(v => !v)} className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm border ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}>
            <FaFilter /> Filters
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
            {[['Status', filterStatus, setFilterStatus, STATUS_OPTIONS],
              ['Visit Type', filterVisitType, setFilterVisitType, VISIT_TYPES],
              ['Contact Type', filterContactType, setFilterContactType, CONTACT_TYPES],
              ['Project Type', filterProjectType, setFilterProjectType, PROJECT_TYPES],
            ].map(([label, val, setter, opts]) => (
              <div key={label}>
                <label className={labelCls}>{label}</label>
                <select value={val} onChange={e => setter(e.target.value)} className={inputCls}>
                  <option value="">All</option>
                  {opts.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            ))}
            <div>
              <label className={labelCls}>From</label>
              <input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>To</label>
              <input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} className={inputCls} />
            </div>
            <div className="flex items-end col-span-2 md:col-span-1 gap-2">
              <button onClick={handleSearch} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg text-sm">Apply</button>
              <button onClick={() => { setFilterStatus(''); setFilterVisitType(''); setFilterContactType(''); setFilterProjectType(''); setFilterFrom(''); setFilterTo(''); setSearch(''); setTimeout(() => fetchVisits(1), 0); }} className={`flex-1 px-3 py-2 rounded-lg text-sm border ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-100'}`}>Clear</button>
            </div>
          </div>
        )}
      </div>

      {/* Visit Table / Contact View */}
      <div className={card}>
        {loading ? (
          <div className="text-center py-12 text-blue-500">Loading…</div>
        ) : visits.length === 0 ? (
          <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No field visits found</div>
        ) : viewMode === 'contacts' ? (
          /* ── Contact View ── */
          <div className="space-y-2">
            {contactGroups.map(group => (
              <div key={group.key} className={`rounded-xl border overflow-hidden ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                {/* Contact header row */}
                <button
                  onClick={() => setExpandedContact(expandedContact === group.key ? null : group.key)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                    isDarkMode ? 'bg-gray-750 hover:bg-gray-700' : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                    group.contactType === 'Customer' ? 'bg-blue-100 text-blue-700'
                    : group.contactType === 'Reference' ? 'bg-purple-100 text-purple-700'
                    : group.contactType === 'Dealer' ? 'bg-orange-100 text-orange-700'
                    : 'bg-gray-200 text-gray-600'
                  }`}>
                    {group.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{group.name}</div>
                    <div className={`text-xs flex items-center gap-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        group.contactType === 'Customer' ? 'bg-blue-100 text-blue-700'
                        : group.contactType === 'Reference' ? 'bg-purple-100 text-purple-700'
                        : group.contactType === 'Dealer' ? 'bg-orange-100 text-orange-700'
                        : 'bg-gray-100 text-gray-600'
                      }`}>{group.contactType}</span>
                      {group.number && <span className="flex items-center gap-1"><FaPhone className="text-xs" />{group.number}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-700'
                    }`}>{group.visits.length} visit{group.visits.length !== 1 ? 's' : ''}</span>
                    {/* Status mini-badges */}
                    <div className="flex gap-1">
                      {Object.entries(
                        group.visits.reduce((acc, v) => { acc[v.status] = (acc[v.status] || 0) + 1; return acc; }, {})
                      ).map(([s, n]) => (
                        <span key={s} className={`text-xs px-1.5 py-0.5 rounded font-medium ${STATUS_COLORS[s] || ''}`}>{s} {n}</span>
                      ))}
                    </div>
                    {expandedContact === group.key ? <FaChevronUp className="text-xs text-gray-400" /> : <FaChevronDown className="text-xs text-gray-400" />}
                  </div>
                </button>

                {/* Expanded visit rows */}
                {expandedContact === group.key && (
                  <div className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                    {group.visits.map(v => (
                      <div key={v._id} className={`flex items-center gap-4 px-4 py-3 text-sm ${
                        isDarkMode ? 'bg-gray-800 hover:bg-gray-750' : 'bg-white hover:bg-blue-50'
                      }`}>
                        <span className="font-mono text-xs w-24 shrink-0 text-gray-500">{v.id}</span>
                        <span className={`text-xs shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{formatDate(v.createdAt)}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${
                          v.visitType === 'Revisit'
                            ? isDarkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-700'
                            : isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-700'
                        }`}>{v.visitType}</span>
                        <span className={`text-xs shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{v.projectType}</span>
                        <span className="flex-1 text-xs truncate" title={v.location}><FaMapMarkerAlt className="inline text-red-400 mr-1" />{v.location}</span>
                        <span className={`text-xs shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{v.items?.join(', ') || '—'}</span>
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium shrink-0 ${STATUS_COLORS[v.status] || ''}`}>{v.status}</span>
                        <button
                          onClick={() => { setSelectedVisit(v); setShowViewModal(true); }}
                          className={`p-1.5 rounded shrink-0 hover:bg-blue-100 dark:hover:bg-blue-900 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}
                          title="View"
                        ><FaEye /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          /* ── Visit Table (default) ── */
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`text-left ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                  {['ID', 'Date', 'Visit Type', 'Project', 'Contact', 'Location', 'Items', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {visits.map((v, i) => (
                  <tr key={v._id} className={`${i % 2 === 0 ? (isDarkMode ? 'bg-gray-800' : 'bg-white') : (isDarkMode ? 'bg-gray-750' : 'bg-gray-50')} hover:opacity-90 transition-opacity`}>
                    <td className="px-4 py-3 font-mono text-xs">{v.id}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{formatDate(v.createdAt)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${v.visitType === 'Revisit' ? (isDarkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-700') : (isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-700')}`}>{v.visitType}</span>
                    </td>
                    <td className="px-4 py-3 text-xs">{v.projectType}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{contactLabel(v)}</div>
                      <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{v.contactType}</div>
                    </td>
                    <td className="px-4 py-3 max-w-[160px] truncate" title={v.location}>{v.location}</td>
                    <td className="px-4 py-3 text-xs max-w-[120px]">
                      {v.items?.length ? v.items.join(', ') : <span className={isDarkMode ? 'text-gray-500' : 'text-gray-400'}>—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[v.status] || ''}`}>{v.status}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => { setSelectedVisit(v); setShowViewModal(true); }} className={`p-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} title="View"><FaEye /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Page {currentPage} of {totalPages}</span>
            <div className="flex gap-1">
              <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} className="px-3 py-1 rounded border disabled:opacity-40 text-sm hover:bg-blue-50 dark:hover:bg-gray-700">←</button>
              <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} className="px-3 py-1 rounded border disabled:opacity-40 text-sm hover:bg-blue-50 dark:hover:bg-gray-700">→</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Create Form Modal ── */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-10 pb-6 overflow-y-auto" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className={`w-full max-w-2xl mx-4 rounded-2xl shadow-2xl p-6 ${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'}`}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold flex items-center gap-2"><FaWalking className="text-blue-500" /> New Field Visit</h3>
              <button onClick={() => setShowForm(false)} className={`p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}><FaTimes /></button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Visit Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Visit Type *</label>
                  <div className="flex gap-2">
                    {VISIT_TYPES.map(vt => (
                      <button type="button" key={vt}
                        onClick={() => setForm(p => ({ ...p, visitType: vt, previousVisit: '' }))}
                        className={`flex-1 py-2 rounded-lg text-sm font-medium border transition-colors ${form.visitType === vt ? 'bg-blue-600 border-blue-600 text-white' : isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-100'}`}>
                        {vt}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Project Type *</label>
                  <select required value={form.projectType} onChange={e => setForm(p => ({ ...p, projectType: e.target.value }))} className={inputCls}>
                    <option value="">Select…</option>
                    {PROJECT_TYPES.map(pt => <option key={pt} value={pt}>{pt}</option>)}
                  </select>
                </div>
              </div>

              {/* Previous Visit (Revisit only) */}
              {form.visitType === 'Revisit' && (
                <div>
                  <label className={labelCls}>Previous Visit *</label>
                  <input
                    value={prevVisitSearch}
                    onChange={e => { setPrevVisitSearch(e.target.value); searchPreviousVisits(e.target.value); }}
                    placeholder="Search by ID or location…"
                    className={inputCls}
                  />
                  {prevVisitLoading && <p className="text-xs text-blue-400 mt-1">Searching…</p>}
                  {prevVisitResults.length > 0 && (
                    <div className={`mt-1 rounded-lg border shadow-lg max-h-36 overflow-y-auto ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                      {prevVisitResults.map(pv => (
                        <button type="button" key={pv._id}
                          onClick={() => { setForm(p => ({ ...p, previousVisit: pv._id })); setPrevVisitSearch(`${pv.id} — ${pv.location}`); setPrevVisitResults([]); }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-gray-600 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                          <span className="font-mono">{pv.id}</span> — {pv.location} <span className={`ml-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>{formatDate(pv.createdAt)}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {form.previousVisit && prevVisitResults.length === 0 && <p className="text-xs text-green-500 mt-1">✓ Selected</p>}
                </div>
              )}

              {/* Contact Type */}
              <div>
                <label className={labelCls}>Contact Type *</label>
                <div className="flex gap-2 flex-wrap">
                  {CONTACT_TYPES.map(ct => (
                    <button type="button" key={ct}
                      onClick={() => { setForm(p => ({ ...p, contactType: ct, customer: '', reference: '', dealer: '', guest: '' })); setSelectedContact(null); setSelectedContactType(null); }}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${form.contactType === ct ? 'bg-blue-600 border-blue-600 text-white' : isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-100'}`}>
                      {ct}
                    </button>
                  ))}
                </div>
              </div>

              {/* Contact selection */}
              {form.contactType && (
                <div>
                  <label className={labelCls}>{form.contactType} *</label>
                  {selectedContact && selectedContactType === form.contactType ? (
                    <div className={`flex items-center justify-between px-3 py-2 rounded-lg border ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-blue-50 border-blue-200'}`}>
                      <span className="text-sm">{getContactDisplayName(selectedContact, selectedContactType)}</span>
                      <button type="button" onClick={() => { setSelectedContact(null); setSelectedContactType(null); setForm(p => ({ ...p, customer: '', reference: '', dealer: '', guest: '' })); }} className="text-red-400 hover:text-red-600"><FaTimes /></button>
                    </div>
                  ) : (
                    <button type="button"
                      onClick={() => setShowMasterSelection(true)}
                      className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${isDarkMode ? 'border-gray-600 text-gray-400 hover:bg-gray-700' : 'border-gray-300 text-gray-500 hover:bg-gray-50'}`}>
                      <FaUser className="inline mr-2" /> Select {form.contactType}…
                    </button>
                  )}
                </div>
              )}

              {/* Contact Person Name + Number */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Contact Person Name *</label>
                  <input
                    required
                    value={form.contactPersonName}
                    onChange={e => setForm(p => ({ ...p, contactPersonName: e.target.value }))}
                    placeholder="Contact person name"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Contact Person Number *</label>
                  <input
                    required
                    pattern="[0-9+\-\s]{7,15}"
                    title="Enter a valid phone number (7-15 digits)"
                    value={form.contactNumber}
                    onChange={e => setForm(p => ({ ...p, contactNumber: e.target.value.replace(/[^0-9+\-\s]/g, '') }))}
                    placeholder="Phone number"
                    className={inputCls}
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className={labelCls}>Location *</label>
                <input required value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder="Visit location" className={inputCls} />
              </div>

              {/* Items */}
              <div>
                <label className={labelCls}>Items</label>
                <Select
                  isMulti
                  options={ITEMS_OPTIONS}
                  value={ITEMS_OPTIONS.filter(o => form.items.includes(o.value))}
                  onChange={sel => setForm(p => ({ ...p, items: sel.map(s => s.value) }))}
                  styles={selectStyles}
                  placeholder="Select items…"
                />
              </div>

              {/* Remarks */}
              <div>
                <label className={labelCls}>Remarks</label>
                <textarea rows={3} value={form.remarks} onChange={e => setForm(p => ({ ...p, remarks: e.target.value }))} placeholder="Notes about the visit…" className={inputCls + ' resize-none'} />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className={`px-4 py-2 rounded-lg text-sm border ${isDarkMode ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 hover:bg-gray-100'}`}>Cancel</button>
                <button type="submit" className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">Save Visit</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── View Modal ── */}
      {showViewModal && selectedVisit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className={`w-full max-w-lg mx-4 rounded-2xl shadow-2xl p-6 ${isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'}`}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold flex items-center gap-2"><FaWalking className="text-blue-500" /> {selectedVisit.id}</h3>
              <button onClick={() => setShowViewModal(false)} className={`p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 ${isDarkMode ? 'text-gray-300' : 'text-gray-500'}`}><FaTimes /></button>
            </div>

            <div className="space-y-3 text-sm">
              {[
                ['Visit Type', <span className={`px-2 py-0.5 rounded text-xs font-medium ${selectedVisit.visitType === 'Revisit' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{selectedVisit.visitType}</span>],
                ['Project Type', selectedVisit.projectType],
                ['Contact', `${contactLabel(selectedVisit)} (${selectedVisit.contactType})`],
                ['Contact Person Name', selectedVisit.contactPersonName || '—'],
                ['Contact Person Number', selectedVisit.contactNumber || '—'],
                ['Location', <span className="flex items-center gap-1"><FaMapMarkerAlt className="text-red-400" />{selectedVisit.location}</span>],
                ['Items', selectedVisit.items?.length ? selectedVisit.items.join(', ') : '—'],
                ['Remarks', selectedVisit.remarks || '—'],
                ['Status', <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[selectedVisit.status]}`}>{selectedVisit.status}</span>],
                ['Created', formatDate(selectedVisit.createdAt)],
                ['Created By', selectedVisit.createdBy?.name || '—'],
              ].map(([label, val]) => (
                <div key={label} className="flex gap-3">
                  <span className={`w-36 font-medium shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{label}</span>
                  <span className="flex-1">{val}</span>
                </div>
              ))}

              {selectedVisit.previousVisit && (
                <div className="flex gap-3">
                  <span className={`w-36 font-medium shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Previous Visit</span>
                  <span>{selectedVisit.previousVisit.id} — {selectedVisit.previousVisit.location}</span>
                </div>
              )}
            </div>

            <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-700">
              {selectedVisit.status === 'Pending' ? (
                <>
                  <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Update Response</p>
                  <div className="grid grid-cols-2 gap-2">
                    {RESPONSE_BUTTONS.map(btn => (
                      <button
                        key={btn.status}
                        onClick={() => handleStatusUpdate(selectedVisit._id, btn.status)}
                        className={`py-2 rounded-lg text-sm font-medium transition-colors ${btn.cls}`}
                      >
                        {btn.label}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <div className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span>Response:</span>
                  <span className={`px-2 py-0.5 rounded font-medium text-xs ${STATUS_COLORS[selectedVisit.status] || ''}`}>{selectedVisit.status}</span>
                  <span className="text-xs italic">(locked)</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MasterSelection Modal */}
      <MasterSelection
        isOpen={showMasterSelection}
        onClose={() => setShowMasterSelection(false)}
        onSelect={(item, type) => { handleMasterSelect(item, type); setShowMasterSelection(false); }}
        allowedTypes={form.contactType ? [form.contactType] : CONTACT_TYPES}
        defaultType={form.contactType || 'Customer'}
        title={`Select ${form.contactType || 'Contact'}`}
      />
    </div>
  );
};

export default FieldVisitManagement;
